/**
 * Contracts shared by every slice of the pipeline.
 *
 * These mirror the reference Python implementation in `src/pop3d` one to one.
 * Constants that shape the picture live in `src/shared/constants.ts`; anything
 * here is pure data shape.
 *
 * Grids are row-major, origin top-left, and every grid in a `PopulationSurface`
 * shares the same `gridWidth`/`gridHeight` and the same affine placement in the
 * normalised projection described by `gridExtent`.
 */

export interface Country {
  /** English display name, e.g. `Italy`. */
  name: string;
  iso2: string;
  iso3: string;
  /** Lowercase, hyphenated; used for output filenames. */
  slug: string;
}

export interface City {
  name: string;
  longitude: number;
  latitude: number;
  population: number;
}

export type View = "top" | "perspective";

/**
 * A city already projected into the normalised plane. The projection closures
 * cannot cross a structured clone, so the worker resolves city positions and
 * the main thread only ever draws them.
 */
export interface PlacedCity {
  name: string;
  x: number;
  y: number;
  /** Used to place the most important labels first when screen space is tight. */
  population: number;
}

/** Mirrors `RenderConfig` in the Python model, including defaults. */
export interface RenderConfig {
  view: View;
  heightScale: number;
  /** 0 extrudes every populated cell; a positive value thins deterministically. */
  maxSpikes: number;
  /** Number of labelled cities, 0 disables them. */
  cities: number;
  title: boolean;
  width: number;
  height: number;
  dpi: number;
  seed: number;
  year: number;
}

export const DEFAULT_CONFIG: RenderConfig = {
  view: "top",
  heightScale: 1,
  maxSpikes: 0,
  cities: 8,
  title: true,
  width: 2800,
  height: 2000,
  dpi: 200,
  seed: 42,
  year: 2020,
};

/**
 * A closed ring as interleaved coordinates: `[x0, y0, x1, y1, …]`.
 * Exterior rings only, matching `geometry.polygon_rings`.
 */
export type Ring = Float64Array;

export interface ProjectedCountry {
  /** Exterior rings in the normalised projection (roughly -0.5…0.5). */
  rings: Ring[];
  /** Exterior rings in projection metres. */
  ringsMeters: Ring[];
  /** `[minX, minY, maxX, maxY]` in projection metres. */
  boundsMeters: [number, number, number, number];
  centerX: number;
  centerY: number;
  /** Metres per unit of the normalised projection. */
  scale: number;
  /** WGS84 lon/lat to projection metres. */
  forward(longitude: number, latitude: number): [number, number];
  /** Projection metres back to WGS84 lon/lat. */
  inverse(x: number, y: number): [number, number];
  /** WGS84 lon/lat straight to the normalised projection. */
  toNormalised(longitude: number, latitude: number): [number, number];
}

/**
 * Everything the renderer needs, all of it derived client-side.
 *
 * `densityGrid` holds people per km² and `NaN` outside the country.
 * `x`/`y`/`density` list only the populated cells, in the normalised
 * projection, matching the Python surface arrays.
 */
export interface PopulationSurface {
  country: Country;
  gridWidth: number;
  gridHeight: number;
  densityGrid: Float32Array;
  gridMask: Uint8Array;
  /** Elevation in metres, clamped at the datum, same grid as the density. */
  elevationGrid: Float32Array;
  /** `[minX, maxX, minY, maxY]` in the normalised projection. */
  gridExtent: [number, number, number, number];
  projectionScale: number;
  sourceYear: number;
  x: Float64Array;
  y: Float64Array;
  density: Float64Array;
}

/**
 * One level of a manually supplied mip chain. Sizes follow
 * `max(1, floor(base / 2^n))`, which is what GL requires.
 */
export interface MipLevel {
  data: Float32Array;
  width: number;
  height: number;
}

/**
 * What the country *is*, carrying no trace of how it is lit.
 *
 * The previous version of this contract had an `ambient` channel holding a
 * baked cast shadow and a difference-of-Gaussians occlusion term, both keyed to
 * one hard-coded sun. Illumination has moved to where it belongs — a shadow map
 * for the sun, a horizon sweep on the GPU for visibility — so every field here
 * is a property of the object: how tall it is, what colour it is, where its
 * coastline runs, and how much slope detail hides below the sampling scale.
 *
 * On the GPU this becomes one R32F height texture carrying `heightMips`, and one
 * RGBA8 texture packing `population`, `terrain`, `coast` and `cosSlope`.
 */
export interface ReliefFields {
  width: number;
  height: number;
  /**
   * Height above the datum in normalised projection units, index 0 at full
   * resolution. Reduction is max-preserving, so a coarse level is a
   * conservative envelope of the spires beneath it.
   */
  heightMips: MipLevel[];
  /** Population colour coordinate, `0…1`. */
  population: Float32Array;
  /** Terrain colour coordinate, `0…1`. */
  terrain: Float32Array;
  /**
   * Signed distance to the coastline in normalised units, positive inside and
   * clamped a couple of cells either side. This *is* the country's silhouette:
   * the fragment shader resolves the edge from it analytically, instead of
   * discarding on whole cells and leaving a staircase.
   */
  coast: Float32Array;
  /**
   * `z` of the full-resolution geometric normal, i.e. the cosine of the local
   * slope. Averaged down the mip chain it states how much slope detail exists
   * below a given scale, which is what lets lost detail become roughness rather
   * than shimmer.
   */
  cosSlope: Float32Array;
  /** `[minX, maxX, minY, maxY]` in the normalised projection. */
  extent: [number, number, number, number];
  /** Cell size in normalised units; the grid is near-square, not exactly square. */
  cellX: number;
  cellY: number;
  /** Tallest point of the field, for the camera's far plane. */
  peak: number;
  /** p99.5 of the height inside the country, which is what the framing uses. */
  reach: number;
}

export interface RenderStats {
  spikes: number;
  populationCells: number;
  densityQ99: number;
}

/** Coarse progress for the UI; every long step reports through this. */
export interface Progress {
  stage: string;
  /** 0…1 when known, otherwise undefined for indeterminate work. */
  fraction?: number;
}

export type ProgressSink = (progress: Progress) => void;
